import { db } from '../config/firebase';
import { collection, doc, getDocs, addDoc, deleteDoc, updateDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import * as jose from 'jose';
import { PMT2SpreadsheetConfig } from '../types/pmt2Types';

// Collection name
const SPREADSHEET_CONFIG_COLLECTION = 'pmt2_spreadsheets';

// JWT Authentication (same pattern as sheetsService.ts)
let cachedToken: string | null = null;
let tokenExpiry: number | null = null;

const PRIVATE_KEY = process.env.REACT_APP_GOOGLE_SHEETS_PRIVATE_KEY?.replace(/\\n/g, '\n');
const CLIENT_EMAIL = process.env.REACT_APP_GOOGLE_SHEETS_CLIENT_EMAIL;

// Create a JWT token (EXACT copy from working sheetsService.ts)
const createJWT = async (): Promise<{ token: string; expiresIn: number }> => {
  if (!PRIVATE_KEY || !CLIENT_EMAIL) {
    throw new Error('Missing service account credentials');
  }

  const now = Math.floor(Date.now() / 1000);
  const expiresIn = 3600; // 1 hour
  const expiry = now + expiresIn;

  const privateKey = await jose.importPKCS8(PRIVATE_KEY, 'RS256');
  const jwt = await new jose.SignJWT({
    scope: 'https://www.googleapis.com/auth/spreadsheets'
  })
    .setProtectedHeader({ alg: 'RS256', typ: 'JWT' })
    .setIssuedAt(now)
    .setIssuer(CLIENT_EMAIL)
    .setSubject(CLIENT_EMAIL)
    .setAudience('https://sheets.googleapis.com/')
    .setExpirationTime(expiry)
    .sign(privateKey);

  return { token: jwt, expiresIn };
};

// Get a valid token, using cache if available (EXACT copy from working sheetsService.ts)
const getAuthToken = async (): Promise<string> => {
  if (cachedToken && tokenExpiry && Date.now() < tokenExpiry) {
    return cachedToken;
  }

  const { token, expiresIn } = await createJWT();
  cachedToken = token;
  tokenExpiry = Date.now() + (expiresIn * 1000);
  return token;
};

/**
 * Checks if a Google Spreadsheet is accessible via the Google Sheets API and if the required sheet exists.
 * @param spreadsheetId The ID of the Google Sheets spreadsheet
 * @returns Object indicating accessibility, potential error, and details.
 */
export const checkSpreadsheetAccess = async (spreadsheetId: string): Promise<{ accessible: boolean; error?: string; details?: any }> => {
  try {
    console.log(`Checking access for spreadsheet ID: ${spreadsheetId}`);

    // Get auth token using the same method as sheetsService.ts
    const authToken = await getAuthToken();
    console.log(`Auth token obtained for checkSpreadsheetAccess: ${authToken ? 'Yes' : 'No'}`);

    if (!authToken) {
      console.error('No auth token available for Google Sheets access');
      return {
        accessible: false,
        error: 'Authentication failed. No auth token available.'
      };
    }

    try {
      // Try to get spreadsheet metadata using fetch (same pattern as sheetsService.ts)
      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`API Error Status: ${response.status}`);
        console.error(`API Error Details:`, errorData);

        if (response.status === 403) {
          return {
            accessible: false,
            error: 'Access denied. Ensure spreadsheet is shared with service account (Editor access).',
            details: errorData
          };
        } else if (response.status === 404) {
          return {
            accessible: false,
            error: 'Spreadsheet not found. Check the ID.',
            details: errorData
          };
        }
        return {
          accessible: false,
          error: `API error: ${errorData.error?.message || response.statusText}`,
          details: errorData
        };
      }

      const data = await response.json();
      const spreadsheetTitle = data.properties?.title || 'Unknown title';
      console.log(`Successfully accessed spreadsheet metadata:`, spreadsheetTitle);

      // Check if the required sheet exists
      const sheets = data.sheets || [];
      const requiredSheetName = 'Client Bundle SKUs'; // Hardcoded for now, consider making dynamic if needed
      const clientBundleSheet = sheets.find((sheet: any) =>
        sheet.properties?.title?.toLowerCase() === requiredSheetName.toLowerCase()
      );

      if (!clientBundleSheet) {
        const availableSheets = sheets.map((s: any) => s.properties?.title).join(', ') || 'None';
        console.error(`Sheet "${requiredSheetName}" not found in spreadsheet. Available sheets:`, availableSheets);
        return {
          accessible: false,
          error: `Sheet "${requiredSheetName}" not found. Available: ${availableSheets}`,
          details: {
            spreadsheetTitle: spreadsheetTitle,
            availableSheets: sheets.map((s: any) => s.properties?.title)
          }
        };
      }

      console.log(`Found required sheet "${requiredSheetName}"`);
      return {
        accessible: true,
        details: {
          spreadsheetTitle: spreadsheetTitle,
          sheetName: clientBundleSheet.properties?.title,
        }
      };
    } catch (error) {
      console.error('Error during API call:', error);
      if (error instanceof Error) {
        return { accessible: false, error: `Error: ${error.message}`, details: { message: error.message } };
      }
      return { accessible: false, error: 'Unknown error during API call', details: { error } };
    }
  } catch (error) {
    console.error('Error checking spreadsheet access:', error);
    if (error instanceof Error) {
      return { accessible: false, error: `Error: ${error.message}`, details: { message: error.message } };
    }
    return { accessible: false, error: 'Unknown error occurred', details: { error } };
  }
};

/**
 * Fetches all spreadsheet configurations, ordered by company name.
 * @returns Array of PMT2SpreadsheetConfig objects.
 */
export const getSpreadsheetConfigs = async (): Promise<PMT2SpreadsheetConfig[]> => {
  const configsQuery = query(
    collection(db, SPREADSHEET_CONFIG_COLLECTION),
    orderBy('companyName')
  );
  const snapshot = await getDocs(configsQuery);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as PMT2SpreadsheetConfig));
};

/**
 * Adds a new spreadsheet configuration after validation.
 * @param spreadsheetId The Google Sheets ID.
 * @param companyName The company name associated with the sheet.
 * @returns The ID of the newly created Firestore document.
 * @throws Error if validation fails or Firestore operation fails.
 */
export const addSpreadsheetConfig = async (spreadsheetId: string, companyName: string): Promise<string> => {
  console.log(`Attempting to add spreadsheet config - ID: ${spreadsheetId}, Company: ${companyName}`);

  try {
    // Check for duplicates
    console.log(`Checking if spreadsheet ID ${spreadsheetId} or company name ${companyName} already exists`);
    const existingConfigsQuery = query(collection(db, SPREADSHEET_CONFIG_COLLECTION));
    const snapshot = await getDocs(existingConfigsQuery);

    const existingBySpreadsheetId = snapshot.docs.find(doc =>
      doc.data().spreadsheetId === spreadsheetId
    );
    if (existingBySpreadsheetId) {
      console.error(`Spreadsheet with ID ${spreadsheetId} already exists`);
      throw new Error(`A spreadsheet with ID ${spreadsheetId} is already configured`);
    }

    const existingByCompanyName = snapshot.docs.find(doc =>
      doc.data().companyName === companyName
    );
    if (existingByCompanyName) {
      console.error(`Company name ${companyName} already exists`);
      throw new Error(`A spreadsheet for company ${companyName} is already configured`);
    }

    // Validate spreadsheet access and required sheet presence
    console.log(`Validating spreadsheet ID: ${spreadsheetId}`);
    const accessCheck = await checkSpreadsheetAccess(spreadsheetId); // Call local function

    if (!accessCheck.accessible) {
      console.error(`Spreadsheet validation failed:`, accessCheck.error);
      console.error(`Validation details:`, accessCheck.details);
      // Throw the specific error from checkSpreadsheetAccess
      throw new Error(accessCheck.error || 'Failed to validate spreadsheet access.');
    } else {
      console.log(`Spreadsheet validation successful`);
      console.log(`Spreadsheet details:`, accessCheck.details);
    }

    // Add the new config document
    const docRef = await addDoc(collection(db, SPREADSHEET_CONFIG_COLLECTION), {
      spreadsheetId,
      companyName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    console.log(`Successfully added spreadsheet config with ID: ${docRef.id}`);
    return docRef.id;
  } catch (error: any) {
    console.error(`Error adding spreadsheet config:`, error);
    // Rethrow the error to be handled by the caller
    throw error;
  }
};

/**
 * Updates the company name for an existing spreadsheet configuration.
 * @param configId The Firestore document ID of the configuration to update.
 * @param newCompanyName The new company name.
 */
export const updateSpreadsheetConfig = async (configId: string, newCompanyName: string): Promise<void> => {
  try {
    console.log(`Updating spreadsheet config ${configId} to company name: ${newCompanyName}`);

    // Check for duplicates (case insensitive)
    const existingConfigsQuery = query(collection(db, SPREADSHEET_CONFIG_COLLECTION));
    const snapshot = await getDocs(existingConfigsQuery);
    const existingByCompanyName = snapshot.docs.find(doc =>
      doc.id !== configId && doc.data().companyName.toLowerCase() === newCompanyName.toLowerCase()
    );
    if (existingByCompanyName) {
      console.error(`Company name ${newCompanyName} already exists`);
      throw new Error(`A spreadsheet with company name ${newCompanyName} is already configured`);
    }

    await updateDoc(doc(db, SPREADSHEET_CONFIG_COLLECTION, configId), {
      companyName: newCompanyName,
      updatedAt: serverTimestamp()
    });

    console.log(`Successfully updated spreadsheet config ${configId}`);
  } catch (error) {
    console.error('Error updating spreadsheet config:', error);
    throw error;
  }
};

/**
 * Removes a spreadsheet configuration document from Firestore.
 * @param configId The Firestore document ID of the configuration to remove.
 */
export const removeSpreadsheetConfig = async (configId: string): Promise<void> => {
  await deleteDoc(doc(db, SPREADSHEET_CONFIG_COLLECTION, configId));
};